import React from 'react';
import PropTypes from 'prop-types';

/**
 * Picture - Renders a responsive <picture> element with multiple sources.
 *
 * Use this component when you need responsive images with different
 * sources for different screen sizes or formats.
 *
 * @example
 * <Picture
 *   src="/images/desktop.webp"
 *   alt="Hero image"
 *   sources={[
 *     { media: '(max-width: 767px)', srcSet: '/images/mobile.webp' },
 *     { media: '(min-width: 768px)', srcSet: '/images/desktop.webp' },
 *   ]}
 *   className="hero-image"
 *   pictureClassName="hero-picture"
 * />
 */
function Picture({
	src,
	alt,
	sources,
	className = '',
	pictureClassName = '',
	loading = 'lazy',
	fetchPriority,
	decoding = 'async',
	...rest
}) {
	const imgProps = {
		src,
		className,
		loading,
		decoding,
		...rest,
	};

	if (fetchPriority) {
		imgProps.fetchpriority = fetchPriority;
	}

	return (
		<picture className={pictureClassName}>
			{sources.map((source, index) => (
				<source
					key={`${source.srcSet}-${index}`}
					media={source.media}
					srcSet={source.srcSet}
					type={source.type}
					sizes={source.sizes}
				/>
			))}
			<img
				alt={alt}
				{...imgProps}
			/>
		</picture>
	);
}

Picture.propTypes = {
	/** Fallback image source URL */
	src: PropTypes.string.isRequired,
	/** Alt text for accessibility */
	alt: PropTypes.string.isRequired,
	/** Responsive image sources */
	sources: PropTypes.arrayOf(
		PropTypes.shape({
			/** Media query for this source (e.g., '(max-width: 767px)') */
			media: PropTypes.string,
			/** Source URL(s) */
			srcSet: PropTypes.string.isRequired,
			/** MIME type (e.g., 'image/webp') */
			type: PropTypes.string,
			/** Sizes attribute for responsive images */
			sizes: PropTypes.string,
		}),
	).isRequired,
	/** CSS class for the <img> element */
	className: PropTypes.string,
	/** CSS class for the <picture> wrapper element */
	pictureClassName: PropTypes.string,
	/** Native loading attribute ('lazy' | 'eager') */
	loading: PropTypes.oneOf(['lazy', 'eager']),
	/** Fetch priority hint ('high' | 'low' | 'auto') */
	fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
	/** Decoding hint ('sync' | 'async' | 'auto') */
	decoding: PropTypes.oneOf(['sync', 'async', 'auto']),
};

export default Picture;
